import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const SyncCtx = createContext({ state: {}, subscribe: () => () => {} });

const POLL_INTERVAL_MS = 2000;

export function SyncProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({});
  const stateRef = useRef({});
  const subsRef = useRef(new Map()); // domain -> Set of callbacks
  const timerRef = useRef(null);

  const poll = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get("/sync/state");
      const next = res.data || {};
      const prev = stateRef.current;
      const changedDomains = [];
      for (const k of Object.keys(next)) {
        if (next[k] !== prev[k]) {
          changedDomains.push(k);
        }
      }
      if (changedDomains.length > 0) {
        stateRef.current = next;
        setState(next);
        // notifică subscribers, silent
        for (const dom of changedDomains) {
          const subs = subsRef.current.get(dom);
          if (subs) {
            for (const cb of subs) {
              try { cb(); } catch {}
            }
          }
        }
      }
    } catch {
      // silent on errors (offline, 401, etc.)
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      stateRef.current = {};
      setState({});
      return;
    }
    // primul fetch imediat
    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [user, poll]);

  const subscribe = useCallback((domains, callback) => {
    const list = Array.isArray(domains) ? domains : [domains];
    for (const dom of list) {
      if (!subsRef.current.has(dom)) subsRef.current.set(dom, new Set());
      subsRef.current.get(dom).add(callback);
    }
    return () => {
      for (const dom of list) {
        const set = subsRef.current.get(dom);
        if (set) set.delete(callback);
      }
    };
  }, []);

  return (
    <SyncCtx.Provider value={{ state, subscribe }}>
      {children}
    </SyncCtx.Provider>
  );
}

/**
 * Hook: re-execută `callback` ori de câte ori signature-ul oricărui domeniu din `domains` se schimbă.
 * Domenii valide: schedule, projects, regii, vacations, inventory, inventory_history, persons.
 */
export function useSyncWatch(domains, callback) {
  const { subscribe } = useContext(SyncCtx);
  const cbRef = useRef(callback);
  useEffect(() => { cbRef.current = callback; }, [callback]);
  useEffect(() => {
    const unsub = subscribe(domains, () => {
      const fn = cbRef.current;
      if (typeof fn === "function") fn();
    });
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.isArray(domains) ? domains.join(",") : domains]);
}

export const useSync = () => useContext(SyncCtx);
